import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  BookOpen, FileText, CheckCircle, AlertCircle, ChevronLeft, 
  Send, Sparkles, PenLine, Eye, Award, TrendingUp,
  MessageSquare, ChevronRight, Calendar, Hash, Loader2, Star,
  Zap, Brain, ThumbsUp, Lightbulb, RefreshCw, Clock, AlertTriangle
} from 'lucide-react';
import DOMPurify from 'dompurify';
import { ApiService } from '../services/api';
import { useAuth } from '../App';
import type { Journal, Entry, Submission, Feedback } from '../types';
import { RichTextEditor } from '../components/RichTextEditor';

// Flow states for seamless experience
type FlowState = 'idle' | 'writing' | 'submitting' | 'evaluating' | 'complete' | 'error';

export const StudentDashboard: React.FC = () => {
  const { user } = useAuth();
  const [journals, setJournals] = useState<Journal[]>([]);
  const [selectedJournal, setSelectedJournal] = useState<Journal | null>(null);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [submissions, setSubmissions] = useState<Record<string, Submission>>({});
  const [loading, setLoading] = useState(true);
  
  // Active entry flow state
  const [activeEntry, setActiveEntry] = useState<Entry | null>(null);
  const [flowState, setFlowState] = useState<FlowState>('idle');
  const [reflection, setReflection] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [currentFeedback, setCurrentFeedback] = useState<Feedback | null>(null);
  const [currentSubmission, setCurrentSubmission] = useState<Submission | null>(null);
  const [evalProgress, setEvalProgress] = useState(0);
  const [evalStep, setEvalStep] = useState(0);

  useEffect(() => { loadJournals(); }, []);
  useEffect(() => { if (selectedJournal) loadEntries(selectedJournal.id); }, [selectedJournal]);

  const loadJournals = async () => {
    try { setJournals(await ApiService.getJournals()); }
    catch { toast.error('Failed to load journals'); }
    finally { setLoading(false); }
  };

  const loadEntries = async (journalId: string) => {
    setLoading(true);
    try {
      const data = await ApiService.getEntries(journalId);
      setEntries(data);
      const subs: Record<string, Submission> = {};
      for (const entry of data) {
        try {
          const entrySubs = await ApiService.getEntrySubmissions(entry.id);
          if (entrySubs.length > 0) subs[entry.id] = entrySubs[0];
        } catch {}
      }
      setSubmissions(subs);
    } catch { toast.error('Failed to load entries'); }
    finally { setLoading(false); }
  };

  const handleReflectionChange = (html: string) => {
    setReflection(html);
    const text = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    setWordCount(text ? text.split(' ').filter(w => w.length > 0).length : 0);
  };

  const startWriting = (entry: Entry) => {
    setActiveEntry(entry);
    setFlowState('writing');
    setReflection('');
    setWordCount(0);
    setCurrentFeedback(null);
    setCurrentSubmission(null);
    setEvalProgress(0);
    setEvalStep(0);
  };

  // Helper function to get word count status
  const getWordCountStatus = (entry: Entry) => {
    const minWords = entry.min_words || 100;
    const maxWords = entry.max_words || 10000;
    
    if (wordCount > maxWords) {
      return { status: 'over', color: 'bg-red-100 text-red-700', message: `${wordCount - maxWords} words over limit` };
    } else if (wordCount >= minWords && wordCount <= maxWords) {
      return { status: 'valid', color: 'bg-green-100 text-green-700', message: 'Within range' };
    } else if (wordCount >= minWords * 0.7) {
      return { status: 'approaching', color: 'bg-amber-100 text-amber-700', message: `${minWords - wordCount} more words needed` };
    } else {
      return { status: 'below', color: 'bg-slate-100 text-slate-600', message: `${minWords - wordCount} more words needed` };
    }
  };

  // Check if submission is allowed
  const canSubmit = (entry: Entry) => {
    const minWords = entry.min_words || 100;
    const maxWords = entry.max_words || 10000;
    return wordCount >= minWords && wordCount <= maxWords;
  };

  const handleSubmit = async () => {
    if (!activeEntry || !canSubmit(activeEntry)) return;
    
    setFlowState('submitting');
    
    try {
      const result = await ApiService.submitReflection(activeEntry.id, reflection);
      setFlowState('evaluating');
      setEvalProgress(0);
      setEvalStep(0);
      
      // Start polling for results with animated progress
      pollForFeedback(result.id);
    } catch (error: any) {
      toast.error(error.message || 'Submission failed');
      setFlowState('error');
    }
  };

  const pollForFeedback = useCallback(async (submissionId: string) => {
    let polls = 0;
    const maxPolls = 90;
    
    // Simulate progress animation
    const progressInterval = setInterval(() => {
      setEvalProgress(prev => {
        if (prev >= 95) return prev;
        return prev + Math.random() * 3;
      });
      setEvalStep(prev => (prev + 1) % 4);
    }, 800);
    
    const poll = async () => {
      try {
        const data = await ApiService.getSubmission(submissionId);
        setCurrentSubmission(data.submission);
        setCurrentFeedback(data.feedback);
        
        if (data.submission.status === 'feedback_ready' || data.submission.status === 'graded') {
          clearInterval(progressInterval);
          setEvalProgress(100);
          
          // Small delay for smooth transition
          setTimeout(() => {
            setFlowState('complete');
            if (activeEntry) {
              setSubmissions(prev => ({ ...prev, [activeEntry.id]: data.submission }));
            }
          }, 500);
        } else if (data.submission.status === 'pending_review') {
          // AI unavailable - but submission saved, teacher will review manually
          clearInterval(progressInterval);
          setCurrentSubmission(data.submission);
          if (data.feedback) setCurrentFeedback(data.feedback);
          setSubmissions(prev => ({ ...prev, [activeEntry!.id]: data.submission }));
          setFlowState('complete'); // Show as complete, not error
        } else if (data.submission.status === 'error') {
          clearInterval(progressInterval);
          setFlowState('error');
        } else if (polls < maxPolls) {
          polls++;
          setTimeout(poll, 2000);
        } else {
          clearInterval(progressInterval);
          setFlowState('error');
        }
      } catch {
        if (polls < maxPolls) {
          polls++;
          setTimeout(poll, 2000);
        }
      }
    };
    
    poll();
  }, [activeEntry]);

  const viewExistingFeedback = async (entry: Entry, submission: Submission) => {
    setActiveEntry(entry);
    setLoading(true);
    try {
      const data = await ApiService.getSubmission(submission.id);
      setCurrentSubmission(data.submission);
      setCurrentFeedback(data.feedback);
      setFlowState('complete');
    } catch {
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const resetFlow = () => {
    setActiveEntry(null);
    setFlowState('idle');
    setReflection('');
    setWordCount(0);
    setCurrentFeedback(null);
    setCurrentSubmission(null);
    if (selectedJournal) loadEntries(selectedJournal.id);
  };

  // ============ LOADING STATE ============
  if (loading && journals.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Loading your journals...</p>
        </div>
      </div>
    );
  }

  // ============ JOURNAL SELECTION ============
  if (!selectedJournal) {
    return (
      <div className="space-y-6">
        {/* Welcome Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-slate-800 mb-2">
            Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}! 👋
          </h1>
          <p className="text-slate-500">Select a journal to continue your reflection journey</p>
        </div>

        {journals.length === 0 ? (
          <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">No Journals Yet</h3>
            <p className="text-slate-500 text-sm">Your teacher will enroll you in a reflection journal soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {journals.map((journal) => (
              <button
                key={journal.id}
                onClick={() => setSelectedJournal(journal)}
                className="bg-white p-6 rounded-lg border border-slate-200 text-left hover:shadow-md hover:border-slate-300 transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <BookOpen className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-800 mb-1">
                      {journal.title}
                    </h3>
                    <p className="text-slate-500 text-sm line-clamp-2">
                      {journal.description || 'Start your reflection learning journey'}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ============ ACTIVE ENTRY VIEW (Writing/Submitting/Evaluating/Complete) ============
  if (activeEntry) {
    const wordStatus = getWordCountStatus(activeEntry);
    const minWords = activeEntry.min_words || 100;
    const maxWords = activeEntry.max_words || 10000;
    
    return (
      <div className="space-y-6">
        {/* Back Button and Progress Steps */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <button
            onClick={resetFlow}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-800 font-medium transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            Back to Entries
          </button>
          
          {/* Progress Steps */}
          <div className="flex items-center gap-2 sm:gap-4">
            {[
              { key: 'write', label: 'Write', icon: PenLine, states: ['writing'] },
              { key: 'eval', label: 'Evaluate', icon: Brain, states: ['submitting', 'evaluating'] },
              { key: 'feedback', label: 'Feedback', icon: Award, states: ['complete'] }
            ].map((step, idx) => {
              const isActive = step.states.includes(flowState);
              const isPast = (flowState === 'evaluating' && idx === 0) || 
                             (flowState === 'complete' && idx < 2) ||
                             (flowState === 'submitting' && idx === 0);
              const Icon = step.icon;
              
              return (
                <React.Fragment key={step.key}>
                  {idx > 0 && (
                    <div className={`w-8 sm:w-12 h-0.5 rounded-full transition-colors ${isPast ? 'bg-green-500' : 'bg-slate-200'}`} />
                  )}
                  <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    isActive ? 'bg-blue-100 text-blue-700' : 
                    isPast ? 'bg-green-100 text-green-700' : 
                    'bg-slate-100 text-slate-400'
                  }`}>
                    {isPast ? <CheckCircle className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                    <span className="hidden sm:inline">{step.label}</span>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Entry Info Card */}
        <div className="bg-white rounded-lg border border-slate-200 p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            <div className="p-3 bg-blue-50 rounded-lg flex-shrink-0">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h1 className="text-xl font-semibold text-slate-800 mb-2">{activeEntry.title}</h1>
              <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                {activeEntry.due_date && (
                  <span className="flex items-center gap-1.5 bg-slate-100 px-3 py-1 rounded-full">
                    <Calendar className="w-4 h-4" />
                    Due {new Date(activeEntry.due_date).toLocaleDateString()}
                  </span>
                )}
                <span className="flex items-center gap-1.5 bg-slate-100 px-3 py-1 rounded-full">
                  <Hash className="w-4 h-4" />
                  {minWords} - {maxWords} words
                </span>
              </div>
            </div>
          </div>
            
          {/* Instructions */}
          {flowState === 'writing' && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <h3 className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2">
                <Lightbulb className="w-4 h-4" />
                Instructions
              </h3>
              <div 
                className="text-slate-700 text-sm"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(activeEntry.instructions) }}
              />
            </div>
          )}
        </div>

        {/* ============ WRITING STATE ============ */}
        {flowState === 'writing' && (
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <h2 className="font-semibold text-slate-800 flex items-center gap-2">
                <PenLine className="w-5 h-5 text-blue-600" />
                Your Reflection
              </h2>
              <div className={`px-3 py-1.5 rounded-full font-medium text-sm flex items-center gap-2 ${wordStatus.color}`}>
                <span>{wordCount}</span>
                <span>/ {minWords}-{maxWords} words</span>
                {wordStatus.status === 'valid' && <CheckCircle className="w-4 h-4" />}
                {wordStatus.status === 'over' && <AlertTriangle className="w-4 h-4" />}
              </div>
            </div>
            
            <div className="p-5 sm:p-6">
              <RichTextEditor 
                value={reflection} 
                onChange={handleReflectionChange}
                placeholder="Start writing your reflection here... Share your thoughts, experiences, and insights."
              />
            </div>
            
            {/* Word count progress bar */}
            <div className="px-6">
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all duration-300 rounded-full ${
                    wordCount > maxWords ? 'bg-red-500' :
                    wordCount >= minWords ? 'bg-green-500' : 'bg-blue-500'
                  }`}
                  style={{ width: `${Math.min((wordCount / maxWords) * 100, 100)}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>0</span>
                <span className={wordCount >= minWords ? 'text-green-600 font-medium' : ''}>{minWords} min</span>
                <span className={wordCount > maxWords ? 'text-red-600 font-medium' : ''}>{maxWords} max</span>
              </div>
            </div>
            
            {/* Warning message for exceeding max words */}
            {wordCount > maxWords && (
              <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">
                  Your reflection exceeds the maximum word limit by <strong>{wordCount - maxWords}</strong> words. 
                  Please shorten it to submit.
                </span>
              </div>
            )}
            
            <div className="p-5 sm:p-6 bg-slate-50/50 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-slate-500 text-center sm:text-left">
                {wordCount < minWords 
                  ? `✍️ Write ${minWords - wordCount} more words to submit`
                  : wordCount > maxWords
                  ? `⚠️ Remove ${wordCount - maxWords} words to submit`
                  : '🎉 Great job! Ready to submit!'
                }
              </p>
              <button
                onClick={handleSubmit}
                disabled={!canSubmit(activeEntry)}
                className="w-full sm:w-auto flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-slate-300 disabled:to-slate-400 text-white font-bold rounded-2xl shadow-lg hover:shadow-xl disabled:shadow-none transition-all duration-300 hover:scale-105 disabled:scale-100"
              >
                <Send className="w-5 h-5" />
                Submit Reflection
              </button>
            </div>
          </div>
        )}

        {/* ============ SUBMITTING STATE ============ */}
        {flowState === 'submitting' && (
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-lg border border-white/50 p-12 sm:p-16 text-center">
            <div className="relative w-24 h-24 mx-auto mb-8">
              <div className="absolute inset-0 bg-blue-500/20 rounded-full animate-ping"></div>
              <div className="relative w-24 h-24 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-xl">
                <Send className="w-12 h-12 text-white animate-pulse" />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-slate-800 mb-3">Submitting Your Reflection</h2>
            <p className="text-slate-500 text-lg">Saving your work...</p>
          </div>
        )}

        {/* ============ EVALUATING STATE ============ */}
        {flowState === 'evaluating' && (
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-lg border border-white/50 p-8 sm:p-12 text-center">
            {/* Animated AI Icon */}
            <div className="relative w-32 h-32 mx-auto mb-8">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full animate-pulse"></div>
              <div className="absolute inset-2 bg-gradient-to-r from-purple-500/30 to-pink-500/30 rounded-full animate-ping"></div>
              <div className="relative w-32 h-32 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 rounded-full flex items-center justify-center shadow-2xl">
                <Sparkles className="w-16 h-16 text-white" />
              </div>
              {/* Floating particles */}
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full animate-bounce shadow-lg" style={{ animationDelay: '0s' }}></div>
              <div className="absolute -bottom-1 -left-3 w-4 h-4 bg-blue-400 rounded-full animate-bounce shadow-lg" style={{ animationDelay: '0.3s' }}></div>
              <div className="absolute top-1/2 -right-4 w-3 h-3 bg-green-400 rounded-full animate-bounce shadow-lg" style={{ animationDelay: '0.6s' }}></div>
            </div>
            
            <h2 className="text-3xl font-bold text-slate-800 mb-3">AI is Evaluating Your Work</h2>
            <p className="text-slate-500 text-lg mb-10 max-w-lg mx-auto">
              Our AI is carefully reading and analyzing your reflection to provide personalized feedback.
            </p>
            
            {/* Progress Bar */}
            <div className="max-w-md mx-auto mb-8">
              <div className="flex justify-between text-sm font-medium mb-2">
                <span className="text-slate-600">Analyzing</span>
                <span className="text-purple-600">{Math.round(evalProgress)}%</span>
              </div>
              <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${evalProgress}%` }}
                />
              </div>
            </div>
            
            {/* Animated Steps */}
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { icon: Eye, label: 'Reading content' },
                { icon: Award, label: 'Checking criteria' },
                { icon: Star, label: 'Finding strengths' },
                { icon: MessageSquare, label: 'Writing feedback' }
              ].map((step, i) => {
                const Icon = step.icon;
                const isActive = evalStep === i;
                const isPast = evalProgress > (i + 1) * 20;
                
                return (
                  <div 
                    key={step.label}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                      isActive ? 'bg-purple-100 text-purple-700 scale-110 shadow-md' :
                      isPast ? 'bg-green-100 text-green-700' :
                      'bg-slate-100 text-slate-400'
                    }`}
                  >
                    {isPast ? <CheckCircle className="w-4 h-4" /> : <Icon className={`w-4 h-4 ${isActive ? 'animate-pulse' : ''}`} />}
                    {step.label}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ============ ERROR STATE ============ */}
        {flowState === 'error' && (
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-lg border border-red-200 p-12 sm:p-16 text-center">
            <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-8">
              <AlertCircle className="w-12 h-12 text-red-500" />
            </div>
            <h2 className="text-3xl font-bold text-slate-800 mb-3">Something Went Wrong</h2>
            <p className="text-slate-500 text-lg mb-8 max-w-lg mx-auto">
              Don't worry, your submission was saved. The AI evaluation encountered an issue. Your teacher has been notified.
            </p>
            <button
              onClick={resetFlow}
              className="px-8 py-4 bg-slate-800 hover:bg-slate-900 text-white font-bold rounded-2xl shadow-lg hover:shadow-xl transition-all"
            >
              Back to Entries
            </button>
          </div>
        )}

        {/* ============ COMPLETE STATE - FEEDBACK ============ */}
        {flowState === 'complete' && currentFeedback && (
          <div className="space-y-6">
            {/* Check for pending manual review (AI unavailable - graceful handling) */}
            {currentFeedback.ai_feedback?.pending_manual_review ? (
              <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-lg border border-amber-200 p-8 text-center">
                <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Clock className="w-10 h-10 text-amber-500" />
                </div>
                <h2 className="text-2xl font-bold text-slate-800 mb-3">✅ Submission Received!</h2>
                <p className="text-slate-600 mb-4 max-w-lg mx-auto text-lg">
                  Your reflection has been submitted successfully.
                </p>
                <p className="text-slate-500 max-w-lg mx-auto">
                  Your teacher will review and provide personalized feedback shortly.
                </p>
                <button
                  onClick={resetFlow}
                  className="mt-6 px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl transition-all"
                >
                  Back to Entries
                </button>
              </div>
            ) : currentFeedback.ai_feedback?.error ? (
              <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-lg border border-red-200 p-8 text-center">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <AlertCircle className="w-10 h-10 text-red-500" />
                </div>
                <h2 className="text-2xl font-bold text-slate-800 mb-3">AI Evaluation Unavailable</h2>
                <p className="text-slate-500 mb-6 max-w-lg mx-auto">
                  The AI system couldn't evaluate your submission. Your teacher has been notified and will review it manually.
                </p>
              </div>
            ) : (
              <>
                {/* Success Banner */}
                <div className="bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 rounded-3xl p-6 sm:p-8 text-white shadow-xl">
                  <div className="flex flex-col sm:flex-row items-center gap-6">
                    <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-10 h-10" />
                    </div>
                    <div className="text-center sm:text-left">
                      <h2 className="text-2xl sm:text-3xl font-bold mb-2">🎉 Feedback Ready!</h2>
                      <p className="text-green-100 text-lg">Your reflection has been evaluated. Great work on completing this entry!</p>
                    </div>
                  </div>
                </div>

                 {/* Teacher Comments (if any) */}
                  {currentFeedback.teacher_comments && (
                    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-3xl p-6 border border-purple-200">
                      <h3 className="font-bold text-purple-800 mb-4 flex items-center gap-2 text-lg">
                        <div className="p-2 bg-purple-200 rounded-xl">
                          <Award className="w-5 h-5 text-purple-700" />
                        </div>
                        Teacher's Comments
                      </h3>
                      <p className="text-purple-800 leading-relaxed">{currentFeedback.teacher_comments}</p>
                    </div>
                  )}
                {/* Main Feedback */}
                {currentFeedback.ai_feedback?.overall_feedback && (
                  <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-lg border border-white/50 overflow-hidden">
                    <div className="p-5 sm:p-6 border-b border-slate-100 flex items-center gap-3">
                      <div className="p-2 bg-indigo-100 rounded-xl">
                        <MessageSquare className="w-6 h-6 text-indigo-600" />
                      </div>
                      <h3 className="font-bold text-xl text-slate-800">AI Feedback</h3>
                    </div>
                    <div className="p-5 sm:p-8">
                      <div 
                        className="prose prose-slate max-w-none prose-p:leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(currentFeedback.ai_feedback.overall_feedback) }}
                      />
                    </div>
                  </div>
                )}

                {/* Strengths & Growth - Side by Side */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Strengths */}
                  {currentFeedback.ai_feedback?.strengths && currentFeedback.ai_feedback.strengths.length > 0 && (
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-6 border border-green-200">
                      <h3 className="font-bold text-green-800 mb-5 flex items-center gap-2 text-lg">
                        <div className="p-2 bg-green-200 rounded-xl">
                          <ThumbsUp className="w-5 h-5 text-green-700" />
                        </div>
                        What You Did Well
                      </h3>
                      <ul className="space-y-4">
                        {currentFeedback.ai_feedback.strengths.map((s: string, i: number) => (
                          <li key={i} className="flex items-start gap-3">
                            <div className="w-7 h-7 bg-green-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Star className="w-4 h-4 text-green-700" />
                            </div>
                            <span className="text-green-800 leading-relaxed">{s}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Growth Opportunities */}
                  {currentFeedback.ai_feedback?.growth_opportunities && currentFeedback.ai_feedback.growth_opportunities.length > 0 && (
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-6 border border-amber-200">
                      <h3 className="font-bold text-amber-800 mb-5 flex items-center gap-2 text-lg">
                        <div className="p-2 bg-amber-200 rounded-xl">
                          <TrendingUp className="w-5 h-5 text-amber-700" />
                        </div>
                        Areas for Growth
                      </h3>
                      <ul className="space-y-4">
                        {currentFeedback.ai_feedback.growth_opportunities.map((g: string, i: number) => (
                          <li key={i} className="flex items-start gap-3">
                            <div className="w-7 h-7 bg-amber-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Zap className="w-4 h-4 text-amber-700" />
                            </div>
                            <span className="text-amber-800 leading-relaxed">{g}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Teacher Comments */}
                {currentFeedback.teacher_comments && (
                  <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-3xl p-6 border border-indigo-200">
                    <h3 className="font-bold text-indigo-800 mb-4 flex items-center gap-2 text-lg">
                      <div className="p-2 bg-indigo-200 rounded-xl">
                        <Award className="w-5 h-5 text-indigo-700" />
                      </div>
                      Teacher's Comments
                    </h3>
                    <p className="text-indigo-800 leading-relaxed whitespace-pre-wrap">{currentFeedback.teacher_comments}</p>
                  </div>
                )}
              </>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-center gap-4 pt-6">
              <button
                onClick={resetFlow}
                className="flex items-center justify-center gap-2 px-8 py-4 bg-slate-800 hover:bg-slate-900 text-white font-bold rounded-2xl shadow-lg hover:shadow-xl transition-all"
              >
                <ChevronLeft className="w-5 h-5" />
                Back to Entries
              </button>
              {activeEntry.allow_resubmission && 
               currentSubmission && currentSubmission.attempt_number < activeEntry.max_attempts && (
                <button
                  onClick={() => {
                    setFlowState('writing');
                    setReflection('');
                    setWordCount(0);
                    setCurrentFeedback(null);
                  }}
                  className="flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold rounded-2xl shadow-lg hover:shadow-xl transition-all"
                >
                  <RefreshCw className="w-5 h-5" />
                  Submit Another Attempt
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ============ ENTRIES LIST VIEW ============
  return (
    <div className="space-y-6">
      {/* Back Button and Journal Header */}
      <div className="flex items-center gap-4 mb-4">
        <button
          onClick={() => setSelectedJournal(null)}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-800 font-medium transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
          All Journals
        </button>
      </div>

      {/* Journal Header */}
      <div className="bg-white rounded-lg border border-slate-200 p-6 mb-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-50 rounded-lg">
            <BookOpen className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-slate-800">{selectedJournal.title}</h1>
            <p className="text-slate-500 text-sm">{selectedJournal.description || 'Your reflection learning journal'}</p>
          </div>
        </div>
      </div>

      {/* Entries List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : entries.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-2">No Entries Yet</h3>
          <p className="text-slate-500 text-sm">Your teacher will add reflection entries soon.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {entries.map((entry) => {
            const submission = submissions[entry.id];
            const canSubmitEntry = !submission || (entry.allow_resubmission && submission.attempt_number < entry.max_attempts);
            const hasFeedback = submission?.status === 'feedback_ready' || submission?.status === 'graded';
            const isProcessing = submission?.status === 'processing';
            const minWords = entry.min_words || 100;
            const maxWords = entry.max_words || 10000;
            
            return (
              <div
                key={entry.id}
                className="bg-white rounded-lg border border-slate-200 overflow-hidden hover:shadow-md transition-shadow"
              >
                <div className="p-5">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    {/* Icon */}
                    <div className={`p-3 rounded-lg flex-shrink-0 ${
                      hasFeedback ? 'bg-green-50' :
                      isProcessing ? 'bg-amber-50' :
                      submission ? 'bg-blue-50' :
                      'bg-slate-100'
                    }`}>
                      {hasFeedback ? <CheckCircle className="w-6 h-6 text-green-600" /> :
                       isProcessing ? <Loader2 className="w-6 h-6 text-amber-600 animate-spin" /> :
                       <FileText className="w-6 h-6 text-slate-500" />}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-800 mb-1">{entry.title}</h3>
                      <div 
                        className="text-slate-500 text-sm mb-3 line-clamp-2"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(entry.instructions.substring(0, 150) + '...') }}
                      />
                      
                      <div className="flex flex-wrap items-center gap-3">
                        {entry.due_date && (
                          <span className="flex items-center gap-1.5 text-sm text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                            <Calendar className="w-4 h-4" />
                            Due {new Date(entry.due_date).toLocaleDateString()}
                          </span>
                        )}
                        <span className="text-sm text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                          {minWords}-{maxWords} words
                        </span>
                        
                        {/* Status Badge */}
                        {submission && (
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            submission.status === 'graded' ? 'bg-purple-100 text-purple-700' :
                            submission.status === 'feedback_ready' ? 'bg-green-100 text-green-700' :
                            submission.status === 'processing' ? 'bg-amber-100 text-amber-700' :
                            submission.status === 'pending_review' ? 'bg-blue-100 text-blue-700' :
                            submission.status === 'error' ? 'bg-red-100 text-red-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {submission.status === 'graded' ? '✓ Graded' :
                             submission.status === 'feedback_ready' ? '✓ Feedback Ready' :
                             submission.status === 'processing' ? '⏳ Evaluating...' :
                             submission.status === 'pending_review' ? '📝 Under Review' :
                             submission.status === 'error' ? '⚠ Error' :
                             'Submitted'}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 w-full sm:w-auto">
                      {hasFeedback && (
                        <button
                          onClick={() => viewExistingFeedback(entry, submission)}
                          className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          View Feedback
                        </button>
                      )}
                      
                      {canSubmitEntry && !isProcessing && (
                        <button
                          onClick={() => startWriting(entry)}
                          className="flex items-center justify-center gap-2 px-4 py-2 bg-[#1e3a5f] hover:bg-[#152a45] text-white text-sm font-medium rounded-md transition-colors"
                        >
                          <PenLine className="w-4 h-4" />
                          {submission ? 'Resubmit' : 'Write Reflection'}
                        </button>
                      )}
                      
                      {submission && (
                        <span className="text-xs text-slate-400 text-center">
                          Attempt {submission.attempt_number} of {entry.max_attempts}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};